import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser'
import { AuthenticationResponseJSON } from '@simplewebauthn/types'
import { useNavigate } from '@tanstack/react-router'
import type { JwtTokenPayload } from 'shared/jwt'
import { Button } from 'ui/button'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import type { components } from '~/api/types'
import { LS_JWT_KEY } from '~/const/api'
import { useAccountStore } from '~/stores/account'
import { useSettingsStore } from '~/stores/settings'
import { decodeJwt } from '~/utils/decode-jwt'
import { getPostAuthRedirectRoute } from '~/utils/get-post-auth-redirect-route'

import { browserSupportsWebAuthnAutofill } from './utils'

export const PasswordlessLogin = () => {
  const navigate = useNavigate()
  const [isAutoFillSupported, setIsAutoFillSupported] = useState(false)
  const [isAutoFillSupportedLoaded, setIsAutoFillSupportedLoaded] = useState(false)
  const { t } = useTranslation()
  const patchAccount = useAccountStore(state => state.patchAccount)
  const setLanguagesIfEmpty = useSettingsStore(state => state.setLanguagesIfEmpty)

  const sendCredentials = useCallback(
    async (credentials: AuthenticationResponseJSON & { signedChallenge: string }) => {
      try {
        const { data: { jwtToken, user } = {} } = await apiClient.POST('/webauthn/login-complete', {
          body: credentials as components['schemas']['LoginCompleteRequestDto'],
        })
        if (!jwtToken || !user) return toast.error(t('Something went wrong'))

        localStorage.setItem(LS_JWT_KEY, jwtToken)
        patchAccount({
          ...user,
          coins: user.coins ?? 0,
          lastSelectedLanguages: user.lastSelectedLanguages as [string, string],
        })

        // TODO: refine api generated style to return exactly [string, string]
        setLanguagesIfEmpty(user.lastSelectedLanguages as [string, string])

        const jwtPayload = decodeJwt<JwtTokenPayload>(jwtToken)

        return navigate({ to: getPostAuthRedirectRoute(jwtPayload?.subscriptionStatus) })
      } catch (error) {
        toast.error(t('Network error'))
        console.error({ message: 'Network error', error })
        return
      }
    },
    [navigate, patchAccount, setLanguagesIfEmpty, t],
  )

  useEffect(() => {
    if (!browserSupportsWebAuthn()) return

    void browserSupportsWebAuthnAutofill().then(async supported => {
      setIsAutoFillSupported(supported)
      setIsAutoFillSupportedLoaded(true)
      if (!supported) return

      try {
        const { data } = await apiClient.GET('/webauthn/login')
        if (!data) return

        const { signedChallenge, ...options } = data

        const credentials = await startAuthentication(options satisfies PublicKeyCredentialRequestOptionsJSON, true)
        await sendCredentials({ ...credentials, signedChallenge })
      } catch (error) {
        console.error({ message: 'Failed to start authentication', error })
      }
    })
  }, [sendCredentials])

  if (!browserSupportsWebAuthn()) return null

  if (!isAutoFillSupportedLoaded || isAutoFillSupported) return null

  const handleLogin = async () => {
    try {
      const { data } = await apiClient.GET('/webauthn/login')

      if (!data) return toast.error(t('Something went wrong'))

      const { signedChallenge, ...options } = data

      const credentials = await startAuthentication(options)
      await sendCredentials({ ...credentials, signedChallenge })
    } catch (error) {
      console.error({ message: 'Failed to start authentication', error })
      if ((error as Error)?.name === 'NotAllowedError') return toast.error(t('Use settings to register passwordless'))

      toast.error(t('Something went wrong'))
    }
  }

  return (
    <Button onClick={handleLogin} type="button">
      {t('Login without password')}
    </Button>
  )
}
